import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, ListRenderItem, StyleSheet } from 'react-native';

import { Box, Row } from '@/components/lib/components';
import { DesktopActionButton } from '@/feature/desktop-nav/components/DesktopActionButton';

import { useDesktopCarousel } from '../../hooks/use-desktop-carousel';
import { LayoutHeaderProps, useLayoutHeaderLink } from '../../hooks/use-layout-header-link';
import { CARD_GAP } from '../../utils/constants';
import { LayoutHeader } from './LayoutHeader';
import { ViewAllCard } from './ViewAllCard';

type DesktopCarouselProps<T> = {
    data: T[];
    itemWidth: number;
    visibleCount: number;
    keyExtractor: (item: T, index: number) => string;
    renderItem: ListRenderItem<T>;
    layoutHeaderProps?: LayoutHeaderProps;
    viewAllHeight?: number;
};

const CardGap = () => <Box width={CARD_GAP} />;

export const DesktopCarousel = <T,>({
    data,
    itemWidth,
    visibleCount,
    keyExtractor,
    renderItem,
    layoutHeaderProps,
    viewAllHeight,
}: DesktopCarouselProps<T>) => {
    const { t } = useTranslation('casino');

    const { isNavigable, navigate: navigateToCategory } = useLayoutHeaderLink(layoutHeaderProps ?? {});
    const hasViewAll = viewAllHeight !== undefined && isNavigable;
    const itemCount = data.length + (hasViewAll ? 1 : 0);

    const { canScrollPrev, canScrollNext, scrollPrev, scrollNext, listProps } = useDesktopCarousel<T>({
        step: itemWidth + CARD_GAP,
        initialCanScrollNext: itemCount > visibleCount,
    });

    if (data.length === 0) {
        return null;
    }

    return (
        <Box>
            <Row alignItems="center" justifyContent="space-between">
                {layoutHeaderProps?.title ? <LayoutHeader {...layoutHeaderProps} /> : <Box />}
                <Row gap="s8" py="s12">
                    <DesktopActionButton
                        variant="prev"
                        onPress={scrollPrev}
                        disabled={!canScrollPrev}
                        accessibilityLabel={t('previous_games')}
                        testID="casino-carousel-prev"
                    />
                    <DesktopActionButton
                        variant="next"
                        onPress={scrollNext}
                        disabled={!canScrollNext}
                        accessibilityLabel={t('next_games')}
                        testID="casino-carousel-next"
                    />
                </Row>
            </Row>
            <FlatList
                {...listProps}
                data={data}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                ItemSeparatorComponent={CardGap}
                horizontal
                showsHorizontalScrollIndicator={false}
                ListFooterComponentStyle={styles.footer}
                ListFooterComponent={
                    hasViewAll ? (
                        <ViewAllCard
                            width={itemWidth}
                            height={viewAllHeight}
                            label={t('view_all')}
                            onPress={navigateToCategory}
                            testID="casino-carousel-view-all"
                        />
                    ) : null
                }
            />
        </Box>
    );
};

const styles = StyleSheet.create({
    footer: {
        marginLeft: CARD_GAP,
    },
});
